// src/App.jsx
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Menu } from 'lucide-react';

// Context & Toast
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { Toaster } from 'react-hot-toast';

// Components
import Header from './components/Header/Header.jsx';
import Sidebar from './components/Sidebar/Sidebar.jsx';
import Footer from './components/common/Footer.jsx';

// Public pages
import Home from './pages/navbar/Home.jsx';
import HowItWorks from './pages/navbar/HowItWorks.jsx';
import CollectionPoints from './pages/navbar/CollectionPoints.jsx';
import Rewards from './pages/navbar/Rewards.jsx';
import About from './pages/navbar/About.jsx';
import Contact from './pages/navbar/Contact.jsx';

// Auth pages
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import VerifyEmail from './pages/VerifyEmail.jsx';

// Lazy-loaded pages
const Dashboard           = lazy(() => import('./pages/Dashboard.jsx'));
const UserDashboard       = lazy(() => import('./pages/UserDashboard.jsx'));
const Notifications       = lazy(() => import('./pages/Notifications.jsx'));
const NgoDashboard        = lazy(() => import('./pages/NgoDashboard.jsx'));
const AdminDashboard      = lazy(() => import('./pages/AdminDashboard.jsx'));
const AdminUserManagement = lazy(() => import('./pages/AdminUserManagement.jsx'));
const AdminReports        = lazy(() => import('./pages/AdminReports.jsx'));
const AdminAnalytics      = lazy(() => import('./pages/AdminAnalytics.jsx'));
const AdminOpportunityManagement = lazy(() => import('./pages/AdminOpportunityManagement.jsx'));
const Profile             = lazy(() => import('./pages/Profile.jsx'));
const Pickups             = lazy(() => import('./pages/Pickups.jsx'));
const Volunteers          = lazy(() => import('./pages/Volunteers.jsx'));
const NGOs                = lazy(() => import('./pages/NGOs.jsx'));
const Reports             = lazy(() => import('./pages/Reports.jsx'));
const Settings            = lazy(() => import('./pages/Settings.jsx'));
const AdminSystemHealth   = lazy(() => import('./pages/AdminSystemHealth.jsx'));
const NgoApplications     = lazy(() => import('./pages/NgoApplications.jsx'));
const MyApplications      = lazy(() => import('./pages/MyApplications.jsx'));
const MyPosted            = lazy(() => import('./pages/MyPosted.jsx'));
const MatchSuggestions    = lazy(() => import('./pages/MatchSuggestions.jsx'));
const VolunteerProfile    = lazy(() => import('./pages/VolunteerProfile.jsx'));
const MatchedVolunteers   = lazy(() => import('./pages/MatchedVolunteers.jsx'));
const AgentPickups        = lazy(() => import('./pages/AgentPickups.jsx'));

// Opportunities
const PublicOpportunities = lazy(() => import('./pages/opportunities/PublicOpportunities.jsx')); // ← new public list
const CreateOpportunity   = lazy(() => import('./pages/opportunities/CreateOpportunity.jsx'));   // renamed from OpportunitiesList
const EditOpportunity     = lazy(() => import('./pages/opportunities/EditOpportunity.jsx'));
const OpportunityApplications = lazy(() => import('./pages/opportunities/OpportunityApplications.jsx'));
const Chat = lazy(() => import('./pages/Chat.jsx'));

// Dashboard Layout
function DashboardLayout({ children }) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  return (
    <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-4.5rem)] overflow-hidden bg-slate-100">
      <Sidebar
        isMobileOpen={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-linear-to-b from-slate-50 to-slate-100">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden">
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 px-3 py-2 text-emerald-700 hover:bg-emerald-50"
            aria-label="Open dashboard menu"
          >
            <Menu size={18} />
            Menu
          </button>
          <p className="text-sm font-semibold text-slate-700">Dashboard</p>
        </div>

        <main className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}

// Protected Route with role check
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-600" />
        <p className="ml-4 text-gray-700 font-medium">Checking authentication...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route wrapper (no auth required)
function PublicRoute({ children }) {
  return children;
}

// Redirect helper: send authenticated user to their role-specific dashboard
function RedirectToRole() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      let dest = '/dashboard';
      if (user.role === 'ngo') dest = '/ngo-dashboard';
      else if (user.role === 'volunteer') dest = '/user-dashboard';
      else if (user.role === 'agent') dest = '/dashboard/assigned-pickups';
      else if (user.role === 'admin') dest = '/admin-dashboard';
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  // while redirecting, show a loader to avoid blank screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-600" />
      <p className="ml-4 text-gray-700 font-medium">Redirecting…</p>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const isDashboardLikeRoute = [
    '/dashboard',
    '/user-dashboard',
    '/ngo-dashboard',
    '/admin-dashboard',
    '/admin/',
    '/profile',
    '/notifications',
    '/chat',
    '/ngo/applications',
    '/my-applications',
    '/pickups',
  ].some((prefix) => location.pathname === prefix || location.pathname.startsWith(prefix));

  return (
    <>
      <Header />

      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-600" />
            <p className="ml-4 text-gray-700 font-medium">Loading...</p>
          </div>
        }
      >
        <Routes>
          {/* ── Public Routes ── */}
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/locations" element={<CollectionPoints />} />
          <Route path="/rewards" element={<Rewards />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />

          {/* Public Opportunities List – anyone can browse & apply */}
          <Route
            path="/opportunities"
            element={
              <PublicRoute>
                {isAuthenticated ? (
                  <DashboardLayout>
                    <PublicOpportunities />
                  </DashboardLayout>
                ) : (
                  <PublicOpportunities />
                )}
              </PublicRoute>
            }
          />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* ── Protected Dashboard Routes ── */}
          <Route
            path="/dashboard"
            element={<ProtectedRoute><RedirectToRole /></ProtectedRoute>}
          />

          {/* Role-Specific Dashboards */}
          <Route
            path="/user-dashboard"
            element={
              <ProtectedRoute allowedRoles={['volunteer']}>
                <DashboardLayout>
                  <UserDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ngo-dashboard"
            element={
              <ProtectedRoute allowedRoles={['ngo']}>
                <DashboardLayout>
                  <NgoDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminUserManagement />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminReports />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminAnalytics />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/opportunities"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminOpportunityManagement />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/system-health"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <AdminSystemHealth />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Notifications */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Notifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Profile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Opportunities – NGO/Admin only */}
          <Route
            path="/dashboard/opportunities"
            element={
              <ProtectedRoute allowedRoles={['ngo', 'admin']}>
                <DashboardLayout>
                  <MyPosted />  {/* Your NGO's posted opportunities list */}
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/opportunities/create"
            element={
              <ProtectedRoute allowedRoles={['ngo']}>
                <DashboardLayout>
                  <CreateOpportunity />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/opportunities/edit/:opportunityId"
            element={
              <ProtectedRoute allowedRoles={['ngo']}>
                <DashboardLayout>
                  <EditOpportunity />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/opportunity-applications/:opportunityId"
            element={
              <ProtectedRoute allowedRoles={['ngo']}>
                <DashboardLayout>
                  <OpportunityApplications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ngo/applications"
            element={
              <ProtectedRoute allowedRoles={['ngo']}>
                <DashboardLayout>
                  <NgoApplications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Chat between users */}
          <Route
            path="/chat/:userId?"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Chat />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Other dashboard pages */}
          <Route
            path="/dashboard/my-applications"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MyApplications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-applications"
            element={<Navigate to="/dashboard/my-applications" replace />}
          />

          <Route
            path="/dashboard/match-suggestions"
            element={
              <ProtectedRoute allowedRoles={['volunteer']}>
                <DashboardLayout>
                  <MatchSuggestions />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Volunteer Profile - for NGOs/Admins */}
          <Route
            path="/dashboard/volunteer/:volunteerId"
            element={
              <ProtectedRoute allowedRoles={['ngo', 'admin']}>
                <DashboardLayout>
                  <VolunteerProfile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Matched Volunteers for Opportunity - NGO only */}
          <Route
            path="/dashboard/matched-volunteers/:opportunityId"
            element={
              <ProtectedRoute allowedRoles={['ngo']}>
                <DashboardLayout>
                  <MatchedVolunteers />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/pickups"
            element={
              <ProtectedRoute allowedRoles={['volunteer', 'ngo']}>
                <DashboardLayout>
                  <Pickups />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/assigned-pickups"
            element={
              <ProtectedRoute allowedRoles={['agent', 'admin']}>
                <DashboardLayout>
                  <AgentPickups />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pickups"
            element={<Navigate to="/dashboard/pickups" replace />}
          />

          <Route
            path="/dashboard/volunteers"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <Volunteers />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/ngos"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardLayout>
                  <NGOs />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'ngo']}>
                <DashboardLayout>
                  <Reports />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {!isDashboardLikeRoute && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 5000,
              style: {
                borderRadius: '10px',
                background: '#1f2937',
                color: '#f3f4f6',
                maxWidth: '500px',
              },
            }}
          />
          <AppShell />
      </Router>
      </SocketProvider>
    </AuthProvider>
  );
}